import { useTranslation } from 'react-i18next';
import { useMembersQuery, useMeQuery, useWorkspaceActivityQuery } from '@platform/state/api';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { appRoutes } from '../../shell/appRoutes';
import { usePendingInvitations } from '../../shell/usePendingInvitations';
import { useTypedRecordLabel } from '../../shell/strings/useTypedRecordLabel';
import { KpiTile } from '../system/KpiTile';
import { SectionCard } from '../system/SectionCard';
import { SystemPage } from '../system/SystemPage';
import { RecentChangeRow } from './RecentChangeRow';
import { useChangesWeek } from './useChangesWeek';
import { useWorkspaceRows } from './useWorkspaceRows';
import './admin.css';

const SECTIONS = ['members', 'roles', 'global-roles', 'workspaces', 'sign-in', 'general', 'audit'];
const RECENT = 8;

/**
 * Огляд «Адміністрування» (прототип v4 G:L7803–7880, pgAdm): «Адміністрування» й «Люди, доступ, робочі простори та налаштування
 * платформи.»; плитки «Учасники · у «простір»», «Запрошення · очікує», «Робочі простори · N ваших» (каталог
 * платформи), «Зміни ·
 * 7 днів · в усіх модулях» (стрічка активності); «Розділи» — сім карток; «Нещодавні зміни» — 8 останніх подій стрічки.
 */
export function AdminHomePage() {
  const { t } = useTranslation();
  const { ws } = useWorkspaceParams();
  const { data: me } = useMeQuery();
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const pending = usePendingInvitations(ws);
  const { count: week } = useChangesWeek(ws);
  // «Нещодавні зміни» — окремий запит 8 подій (той самий, що й «Нещодавня активність» Головної): рядки з'являються одразу, не чекаючи лічильника
  const { data: feed } = useWorkspaceActivityQuery({ ws, limit: RECENT }, { skip: !ws });
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  const spaces = useWorkspaceRows();
  const recent = feed?.items.slice(0, RECENT) ?? [];
  const recordLabel = useTypedRecordLabel(ws, recent.map((i) => i.record.module ?? ''));
  return (
    <SystemPage title={t('sysPages.admin')} sub={t('sysPages.adminHome.sub')}>
      <div className="sp-kpis">
        <KpiTile label={t('sysPages.sections.members')} value={members ? String(members.length) : '…'} note={t('sysPages.adminHome.membersNote', { ws: wsName })} to={appRoutes.admin('members')} />
        <KpiTile label={t('sysPages.sections.invitations')} value={pending === undefined ? '—' : String(pending)} note={t('sysPages.adminHome.invitesNote')} to={appRoutes.admin('invitations')} />
        <KpiTile label={t('sysPages.sections.workspaces')} value={String(spaces.rows.length)} note={t('sysPages.adminHome.yours', { count: spaces.mine })} to={appRoutes.admin('workspaces')} />
        <KpiTile label={t('sysPages.adminHome.changes')} value={week === undefined ? '…' : week ?? '—'} note={t('sysPages.adminHome.changesNote')} to={appRoutes.admin('audit')} />
      </div>
      <div className="sp-two">
        <div className="sp-block">
          <div className="sp-eyebrow">{t('sysPages.adminHome.sections')}</div>
          <div className="sp-cards">
            {SECTIONS.map((k) => <SectionCard key={k} title={t(`sysPages.sections.${k}`)} desc={t(`sysPages.sectionDesc.${k}`)} to={appRoutes.admin(k)} />)}
          </div>
        </div>
        <div className="sp-block">
          <div className="sp-eyebrow">{t('sysPages.adminHome.recent')}</div>
          <div className="sp-table ad-changes">
            {recent.map((item) => <RecentChangeRow key={item.id} item={item} recordLabel={recordLabel(item.record)} />)}
            {feed?.available && !feed.items.length && <div className="sp-empty">{t('sysPages.adminHome.noRecent')}</div>}
            {feed?.available === false && <div className="sp-empty">{t('sysPages.adminHome.noFeed')}</div>}
          </div>
        </div>
      </div>
    </SystemPage>
  );
}
